import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Target, Plus, CurrencyCircleDollar, UserPlus, GraduationCap,
  GlobeHemisphereEast, Lightning, Package, Trash, PencilSimple,
  TrendUp, CheckCircle, XCircle, Clock, X, DownloadSimple, UsersFour, BookmarkSimple, Archive
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import useAuthStore from '../store/authStore';
import { confirm } from '../components/ConfirmDialog';

import { notify } from '../lib/notify';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  'Financial': CurrencyCircleDollar,
  'Customer Acquisition': UserPlus,
  'Learning & Development': GraduationCap,
  'Market Expansion': GlobeHemisphereEast,
  'Operational Excellence': Lightning,
  'Product Mix': Package,
};

const STATUS_STYLES = {
  active: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock, label: 'Active' },
  completed: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle, label: 'Completed' },
  missed: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Missed' },
};

const formatValue = (val, unit) => {
  if (unit === 'INR') {
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(2)} L`;
    if (val >= 1000) return `₹${(val / 1000).toFixed(1)}K`;
    return `₹${Math.round(val)}`;
  }
  return `${Math.round(val * 100) / 100}${unit ? ' ' + unit : ''}`;
};

const periodShorthand = (type, start, end) => {
  const s = new Date(start), e = new Date(end);
  if (type === 'year') return `Year ${s.getFullYear()}`;
  if (type === 'quarter') {
    const q = Math.floor(s.getMonth() / 3) + 1;
    return `Q${q} ${s.getFullYear()}`;
  }
  if (type === 'month') return s.toLocaleString('en', { month: 'short', year: 'numeric' });
  return `${s.toLocaleDateString('en', { day: '2-digit', month: 'short' })} → ${e.toLocaleDateString('en', { day: '2-digit', month: 'short', year: '2-digit' })}`;
};

const Goals = () => {
  const { user } = useAuthStore();
  const canManage = user && ['Vice President', 'President'].includes(user.role);
  // Feb 2026 — templates + bulk-assign are open to NSM too (per spec Q3=a)
  // while one-off Create Goal stays limited to VP/President via canManage.
  const canManageTemplates = user && ['Vice President', 'President', 'National Sales Manager'].includes(user.role);

  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [bscOptions, setBscOptions] = useState({ bsc_parameters: [], measure_types: [], data_sources: [] });
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [performance, setPerformance] = useState(null);

  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [scopeFilter, setScopeFilter] = useState('mine'); // mine | all
  // Feb 2026 — assignee filter for managers: pick a specific SE to view
  // their goal sheet. Ignored when scopeFilter is 'mine' (that's already
  // your own). Empty string = no filter (show every visible SE).
  const [assigneeFilter, setAssigneeFilter] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [progressGoal, setProgressGoal] = useState(null);
  // Feb 2026 — templates + bulk-assign
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showTemplatesPanel, setShowTemplatesPanel] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [prefillFromTemplate, setPrefillFromTemplate] = useState(null);

  const fetchGoals = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/goals`);
      setGoals(resp.data.goals || []);
    } catch (err) {
      console.error('Failed to fetch goals', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPerformance = async () => {
    try {
      const resp = await axios.get(`${API}/goals/performance`);
      setPerformance(resp.data);
    } catch (err) {
      console.error('Failed to fetch performance', err);
    }
  };

  useEffect(() => {
    fetchGoals();
    fetchPerformance();
    axios.get(`${API}/goals/categories`).then(r => {
      setCategories(r.data.categories || []);
      setBscOptions({
        bsc_parameters: r.data.bsc_parameters || [],
        measure_types: r.data.measure_types || [],
        data_sources: r.data.data_sources || [],
      });
    });
    if (canManage) {
      axios.get(`${API}/goals/assignable-users`).then(r => setAssignableUsers(r.data.users || [])).catch(() => {});
    }
    if (canManageTemplates) {
      axios.get(`${API}/goals/assignable-users`).then(r => setAssignableUsers(r.data.users || [])).catch(() => {});
      axios.get(`${API}/goal-templates`).then(r => setTemplates(r.data || [])).catch(() => {});
    }
  }, [canManage, canManageTemplates]);

  const refreshTemplates = () =>
    axios.get(`${API}/goal-templates`).then(r => setTemplates(r.data || [])).catch(() => {});

  const useTemplate = (tpl) => {
    setPrefillFromTemplate(tpl);
    setShowTemplatesPanel(false);
    setShowBulkModal(true);
  };

  const archiveTemplate = async (tpl) => {
    const ok = await confirm({
      title: 'Archive template?',
      message: `Archive "${tpl.name}"? Historical goals stay linked; new bulk-assigns can no longer pick it.`,
      confirmText: 'Archive',
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/goal-templates/${tpl.id}`);
      await refreshTemplates();
    } catch { notify('Failed to archive template'); }
  };

  const visibleGoals = useMemo(() => {
    let items = goals;
    if (scopeFilter === 'mine' && user) items = items.filter(g => g.assigned_to === user.id);
    if (scopeFilter === 'all' && assigneeFilter) items = items.filter(g => g.assigned_to === assigneeFilter);
    if (statusFilter !== 'all') items = items.filter(g => g.status === statusFilter);
    if (categoryFilter !== 'all') items = items.filter(g => g.category === categoryFilter);
    return items;
  }, [goals, scopeFilter, assigneeFilter, statusFilter, categoryFilter, user]);

  const handleDelete = async (goal) => {
    const ok = await confirm({
      title: 'Delete goal?',
      message: `Delete goal "${goal.title}"? This cannot be undone.`,
      confirmText: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/goals/${goal.id}`);
      fetchGoals();
      fetchPerformance();
    } catch (err) {
      notify('Failed to delete goal');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="goals-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 lg:mb-8">
        <div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }} data-testid="goals-title">
            Goals
          </h1>
          <p className="text-[#6D8E87]">Track objectives, progress, and performance</p>
        </div>
        {canManageTemplates && (
          <div className="flex items-center gap-2">
            {canManage && (
            <button
              onClick={() => {
                const token = localStorage.getItem('token');
                if (!token) { notify('Please log in again'); return; }
                const link = document.createElement('a');
                link.href = `${API}/goals-export/excel?token=${encodeURIComponent(token)}`;
                link.setAttribute('download', 'goals_report.xlsx');
                document.body.appendChild(link);
                link.click();
                link.remove();
              }}
              className="flex items-center gap-2 px-5 py-3 border-2 border-[#00a69c] text-[#00a69c] rounded-full font-medium hover:bg-[#E2F3F0] transition"
              data-testid="download-report-btn"
              title="Download Excel report of all goals"
            >
              <DownloadSimple size={20} weight="bold" /> Download Report
            </button>
            )}
            <button
              onClick={() => { setPrefillFromTemplate(null); setShowBulkModal(true); }}
              className="flex items-center gap-2 px-4 py-3 border-2 border-[#00a69c] text-[#00a69c] rounded-full font-medium hover:bg-[#E2F3F0] transition"
              data-testid="bulk-assign-btn"
              title="Assign the same goal to multiple team members"
            >
              <UsersFour size={20} weight="bold" /> Bulk Assign
            </button>
            <button
              onClick={() => setShowTemplatesPanel(true)}
              className="flex items-center gap-2 px-4 py-3 border-2 border-[#00a69c] text-[#00a69c] rounded-full font-medium hover:bg-[#E2F3F0] transition relative"
              data-testid="open-templates-btn"
              title="Manage reusable goal templates"
            >
              <BookmarkSimple size={20} weight="bold" /> Templates
              {templates.length > 0 && (
                <span className="ml-1 bg-[#00a69c] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center" data-testid="templates-count-badge">
                  {templates.length}
                </span>
              )}
            </button>
            {canManage && (
            <button
              onClick={() => { setEditingGoal(null); setShowModal(true); }}
              className="flex items-center gap-2 px-5 py-3 bg-[#00a69c] text-white rounded-full font-medium hover:bg-[#00bfb3] transition"
              data-testid="create-goal-btn"
            >
              <Plus size={20} weight="bold" /> Create Goal
            </button>
            )}
          </div>
        )}
      </div>

      {/* Performance score */}
      {performance && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="performance-grid">
          <div className="medical-card p-5">
            <div className="flex items-center gap-2 mb-2">
              <TrendUp size={18} className="text-[#00a69c]" />
              <span className="text-xs uppercase tracking-wide text-[#6D8E87] font-semibold">Performance Score</span>
            </div>
            <div className="text-3xl font-bold text-[#00a69c]" data-testid="performance-score" style={{ fontFamily: 'Outfit' }}>
              {performance.score}%
            </div>
            <div className="text-xs text-[#6D8E87] mt-1">Weighted across {performance.total_weight} credits</div>
          </div>
          <div className="medical-card p-5">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={18} className="text-blue-600" />
              <span className="text-xs uppercase tracking-wide text-[#6D8E87] font-semibold">Active</span>
            </div>
            <div className="text-3xl font-bold text-blue-600" style={{ fontFamily: 'Outfit' }}>{performance.active_goals}</div>
          </div>
          <div className="medical-card p-5">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle size={18} className="text-emerald-600" />
              <span className="text-xs uppercase tracking-wide text-[#6D8E87] font-semibold">Completed</span>
            </div>
            <div className="text-3xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit' }}>{performance.completed_goals}</div>
          </div>
          <div className="medical-card p-5">
            <div className="flex items-center gap-2 mb-2">
              <XCircle size={18} className="text-red-500" />
              <span className="text-xs uppercase tracking-wide text-[#6D8E87] font-semibold">Missed</span>
            </div>
            <div className="text-3xl font-bold text-red-500" style={{ fontFamily: 'Outfit' }}>{performance.missed_goals}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="goal-filters">
        <div className="flex gap-1 p-1 bg-white border border-[#D5E8E4] rounded-full">
          {['mine', 'all'].map(v => (
            <button key={v} onClick={() => { setScopeFilter(v); if (v === 'mine') setAssigneeFilter(''); }}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition ${scopeFilter === v ? 'bg-[#00a69c] text-white' : 'text-[#3B5A54]'}`}
              data-testid={`scope-${v}`}>
              {v === 'mine' ? 'My Goals' : 'All Visible'}
            </button>
          ))}
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-[#D5E8E4] rounded-lg bg-white" data-testid="filter-status">
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="missed">Missed</option>
        </select>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-[#D5E8E4] rounded-lg bg-white" data-testid="filter-category">
          <option value="all">All Categories</option>
          {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        {/* Feb 2026 — assignee filter (managers only, active when scope=all).
            Derived from what the caller can already see — no extra API call. */}
        {scopeFilter === 'all' && (() => {
          const uniqueAssignees = Array.from(
            new Map(goals.filter(g => g.assigned_to).map(g => [g.assigned_to, {
              id: g.assigned_to, name: g.assigned_to_name, role: g.assigned_to_role,
            }])).values()
          ).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          if (uniqueAssignees.length <= 1) return null;
          return (
            <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-[#D5E8E4] rounded-lg bg-white"
              data-testid="filter-assignee">
              <option value="">All Assignees ({uniqueAssignees.length})</option>
              {uniqueAssignees.map(u => (
                <option key={u.id} value={u.id}>{u.name}{u.role ? ` · ${u.role}` : ''}</option>
              ))}
            </select>
          );
        })()}
        {scopeFilter === 'all' && assigneeFilter && (
          <button onClick={() => setAssigneeFilter('')}
            className="px-2 py-1 text-[11px] text-[#6D8E87] hover:text-[#052A23]"
            data-testid="clear-assignee-filter">
            Clear
          </button>
        )}
      </div>

      {/* Goal list */}
      {loading ? (
        <div className="text-center py-12 text-[#6D8E87]">Loading goals...</div>
      ) : visibleGoals.length === 0 ? (
        <div className="medical-card p-12 text-center" data-testid="no-goals">
          <Target size={48} className="mx-auto text-[#D5E8E4] mb-3" />
          <p className="text-[#6D8E87] text-lg">No goals to show.</p>
          {canManage && <p className="text-[#6D8E87] text-sm mt-1">Click &ldquo;Create Goal&rdquo; to set one.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="goals-grid">
          {visibleGoals.map(goal => {
            const Icon = CATEGORY_ICONS[goal.category] || Target;
            const statusMeta = STATUS_STYLES[goal.status] || STATUS_STYLES.active;
            const StIcon = statusMeta.icon;
            const categoryMeta = categories.find(c => c.value === goal.category);
            const color = categoryMeta?.color || '#00a69c';
            const isOwner = user && goal.assigned_to === user.id;
            const isManual = goal.metric_type === 'manual';
            return (
              <div key={goal.id} className="medical-card p-5 hover:shadow-md transition relative" data-testid={`goal-card-${goal.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}20`, color }}>
                    <Icon size={22} weight="bold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#052A23] leading-tight truncate" style={{ fontFamily: 'Outfit' }}>{goal.title}</h3>
                    <p className="text-xs text-[#6D8E87] mt-0.5">{goal.category}</p>
                  </div>
                  {canManage && (
                    <div className="flex gap-1">
                      <button onClick={() => { setEditingGoal(goal); setShowModal(true); }}
                        className="w-7 h-7 flex items-center justify-center text-[#00a69c] hover:bg-[#E2F3F0] rounded" data-testid={`edit-goal-${goal.id}`}>
                        <PencilSimple size={14} />
                      </button>
                      <button onClick={() => handleDelete(goal)}
                        className="w-7 h-7 flex items-center justify-center text-red-500 hover:bg-red-50 rounded" data-testid={`delete-goal-${goal.id}`}>
                        <Trash size={14} />
                      </button>
                    </div>
                  )}
                </div>

                {goal.description && (
                  <p className="text-xs text-[#3B5A54] mb-3 line-clamp-2">{goal.description}</p>
                )}

                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-[#6D8E87]">
                    {formatValue(goal.current_value || 0, goal.unit)} / <span className="font-semibold text-[#052A23]">{formatValue(goal.target_value, goal.unit)}</span>
                  </span>
                  <span className="text-sm font-bold text-[#00a69c]">{goal.progress_percent}%</span>
                </div>
                <div className="h-2 bg-[#F4F9F7] rounded-full overflow-hidden mb-3">
                  <div className="h-full rounded-full transition-all"
                    style={{ width: `${Math.min(100, goal.progress_percent)}%`, backgroundColor: color }} />
                </div>

                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold ${statusMeta.bg} ${statusMeta.text}`}>
                    <StIcon size={12} weight="bold" /> {statusMeta.label}
                  </span>
                  <span className="px-2 py-0.5 bg-[#F4F9F7] text-[#3B5A54] rounded-full">
                    {periodShorthand(goal.period_type, goal.period_start, goal.period_end)}
                  </span>
                  <span className="px-2 py-0.5 bg-[#FEF3C7] text-[#92400E] rounded-full font-semibold">
                    {goal.credit_weight} credits
                  </span>
                </div>

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F4F9F7]">
                  <div className="text-xs text-[#6D8E87]">
                    <span className="font-medium text-[#3B5A54]">{goal.assigned_to_name}</span>
                    <span className="text-[#6D8E87]"> · {goal.assigned_to_role}</span>
                  </div>
                  {isManual && (isOwner || canManage) && (
                    <button onClick={() => setProgressGoal(goal)}
                      className="text-xs text-[#00a69c] font-medium hover:underline"
                      data-testid={`update-progress-${goal.id}`}>
                      Update Progress
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <GoalModal
          open={showModal}
          onClose={() => { setShowModal(false); setEditingGoal(null); }}
          onSaved={() => { fetchGoals(); fetchPerformance(); }}
          categories={categories}
          bscOptions={bscOptions}
          users={assignableUsers}
          existing={editingGoal}
        />
      )}

      {progressGoal && (
        <ProgressModal
          goal={progressGoal}
          onClose={() => setProgressGoal(null)}
          onSaved={() => { fetchGoals(); fetchPerformance(); setProgressGoal(null); }}
        />
      )}

      {/* Feb 2026 — Bulk Assign modal (multi-select assignees + per-person target override + save-as-template) */}
      {showBulkModal && canManageTemplates && (
        <BulkAssignModal
          open={showBulkModal}
          onClose={() => { setShowBulkModal(false); setPrefillFromTemplate(null); }}
          onSaved={(res) => {
            fetchGoals(); fetchPerformance(); refreshTemplates();
            setShowBulkModal(false); setPrefillFromTemplate(null);
            const parts = [];
            if (res.created_count) parts.push(`✓ ${res.created_count} goal${res.created_count === 1 ? '' : 's'} created`);
            if (res.skipped_count) parts.push(`⚠ ${res.skipped_count} skipped (duplicate)`);
            if (res.saved_template_id) parts.push('★ Template saved');
            notify(parts.join('\n'));
          }}
          categories={categories}
          bscOptions={bscOptions}
          users={assignableUsers}
          templates={templates.filter(t => !t.is_archived)}
          prefillTemplate={prefillFromTemplate}
        />
      )}

      {/* Feb 2026 — Templates panel */}
      {showTemplatesPanel && canManageTemplates && (
        <TemplatesPanel
          open={showTemplatesPanel}
          onClose={() => setShowTemplatesPanel(false)}
          templates={templates}
          onUse={useTemplate}
          onArchive={archiveTemplate}
          onRefresh={refreshTemplates}
        />
      )}
    </div>
  );
};

// Goal create/edit modal — Slice 1 (Feb 2026): expanded with BSC parameter,
// measure type, data source (auto/manual), and a live weightage-remaining
// indicator so the manager doesn't blow past the 100-cap by surprise.
const GoalModal = ({ open, onClose, onSaved, categories, bscOptions, users, existing }) => {
  const defaultStart = new Date().toISOString().slice(0, 10);
  const defaultEnd = new Date(new Date().getFullYear(), 11, 31).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    title: existing?.title || '',
    description: existing?.description || '',
    category: existing?.category || (categories[0]?.value || 'Financial'),
    metric_type: existing?.metric_type || 'revenue_target',
    target_value: existing?.target_value || '',
    unit: existing?.unit || 'INR',
    period_type: existing?.period_type || 'year',
    period_start: existing?.period_start || defaultStart,
    period_end: existing?.period_end || defaultEnd,
    assigned_to: existing?.assigned_to || '',
    credit_weight: existing?.credit_weight || 10,
    // Slice 1 BSC fields
    bsc_parameter: existing?.bsc_parameter || (bscOptions?.bsc_parameters?.[0] || 'Financial'),
    measure_type: existing?.measure_type || 'increasing_number',
    data_source: existing?.data_source || 'auto:won_revenue',
    manual_actual: existing?.manual_actual ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Live weightage-remaining budget for the (assignee, period) pair.
  const [weightSummary, setWeightSummary] = useState(null);

  const selectedCat = categories.find(c => c.value === form.category);
  const availableMetrics = selectedCat?.metrics || [];

  // When category changes, default metric to first available
  useEffect(() => {
    if (!existing && availableMetrics.length && !availableMetrics.find(m => m.value === form.metric_type)) {
      const m = availableMetrics[0];
      setForm(f => ({ ...f, metric_type: m.value, unit: m.unit || f.unit }));
    }
  }, [form.category]); // eslint-disable-line

  // Live weightage remaining. Fires whenever (assignee, period_start,
  // period_end) changes — matches the exact key the backend caps on.
  useEffect(() => {
    if (!form.assigned_to || !form.period_start || !form.period_end) {
      setWeightSummary(null);
      return;
    }
    const excludeParam = existing?.id ? `&exclude_goal_id=${existing.id}` : '';
    axios.get(`${API}/goals/weightage-summary?assigned_to=${form.assigned_to}&period_start=${form.period_start}&period_end=${form.period_end}${excludeParam}`)
      .then(r => setWeightSummary(r.data))
      .catch(() => setWeightSummary(null));
  }, [form.assigned_to, form.period_start, form.period_end, existing?.id]);

  // When data_source changes, auto-suggest the UOM and measure_type from the
  // registry so the user doesn't have to remember (e.g. picking
  // "auto:won_revenue" pre-fills UOM=Rupee, measure=increasing_number).
  // Also clears a stale manual_actual left over from a prior "manual" pick.
  useEffect(() => {
    const ds = (bscOptions?.data_sources || []).find(x => x.value === form.data_source);
    if (!ds) return;
    setForm(f => ({
      ...f,
      unit: ds.uom || f.unit,
      measure_type: ds.measure || f.measure_type,
      // Reset manual_actual when switching to an auto tracker — it would be
      // ignored server-side, but stale numbers confuse users if they toggle
      // back to `manual` later.
      manual_actual: ds.value === 'manual' ? f.manual_actual : 0,
    }));
  }, [form.data_source]); // eslint-disable-line

  // Period presets
  const applyPreset = (type) => {
    const now = new Date();
    let start, end;
    if (type === 'year') {
      start = new Date(now.getFullYear(), 0, 1);
      end = new Date(now.getFullYear(), 11, 31);
    } else if (type === 'quarter') {
      const q = Math.floor(now.getMonth() / 3);
      start = new Date(now.getFullYear(), q * 3, 1);
      end = new Date(now.getFullYear(), q * 3 + 3, 0);
    } else if (type === 'month') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    }
    setForm(f => ({
      ...f,
      period_type: type,
      period_start: start.toISOString().slice(0, 10),
      period_end: end.toISOString().slice(0, 10),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.title || !form.target_value || !form.assigned_to) {
      setError('Please fill Title, Target, and Assignee');
      return;
    }
    setSaving(true);
    try {
      // Slice 1 payload — includes BSC fields; server backfills defaults for
      // any missing key so legacy flows still work.
      const payload = {
        ...form,
        target_value: parseFloat(form.target_value),
        credit_weight: parseInt(form.credit_weight),
        manual_actual: parseFloat(form.manual_actual) || 0,
      };
      if (existing) {
        await axios.put(`${API}/goals/${existing.id}`, payload);
      } else {
        await axios.post(`${API}/goals`, payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save goal');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto" data-testid="goal-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
            {existing ? 'Edit Goal' : 'Create Goal'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className="block text-xs font-medium text-[#3B5A54] mb-1">Title *</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg outline-none focus:ring-2 focus:ring-[#00bfb3]" required data-testid="goal-title" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#3B5A54] mb-1">Description</label>
            <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg outline-none focus:ring-2 focus:ring-[#00bfb3]" data-testid="goal-description" />
          </div>

          {/* Slice 1 (Feb 2026) — BSC-aligned row: parameter + measure + data-source.
              These three together dictate how the goal rolls up (parameter),
              how progress is scored (measure), and where the actual value
              comes from (data source, auto or manual). */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#3B5A54] mb-1">BSC Parameter *</label>
              <select value={form.bsc_parameter}
                onChange={e => setForm({ ...form, bsc_parameter: e.target.value })}
                className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg bg-white" data-testid="goal-bsc-parameter">
                {(bscOptions?.bsc_parameters || []).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#3B5A54] mb-1">Measure *</label>
              <select value={form.measure_type}
                onChange={e => setForm({ ...form, measure_type: e.target.value })}
                className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg bg-white" data-testid="goal-measure-type">
                {(bscOptions?.measure_types || []).map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <p className="text-[10px] text-[#6D8E87] mt-1">Decreasing = lower-is-better (e.g. Outstanding ≤ 5%).</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#3B5A54] mb-1">Data Source *</label>
              <select value={form.data_source}
                onChange={e => setForm({ ...form, data_source: e.target.value })}
                className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg bg-white" data-testid="goal-data-source">
                {(bscOptions?.data_sources || []).map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
          </div>

          {/* Manual actual value — shown only when the goal is not auto-tracked.
              Lets the appraiser (or the assignee, per the PATCH endpoint) key
              in the reported number now, no separate progress modal needed. */}
          {form.data_source === 'manual' && (
            <div>
              <label className="block text-xs font-medium text-[#3B5A54] mb-1">Actual (manual entry)</label>
              <input type="number" step="any" value={form.manual_actual}
                onChange={e => setForm({ ...form, manual_actual: e.target.value })}
                placeholder="e.g. 6"
                className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg" data-testid="goal-manual-actual" />
              <p className="text-[10px] text-[#6D8E87] mt-1">Update this whenever the reported number changes. Progress % is derived from Measure + Target.</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#3B5A54] mb-1">Category (legacy — for reports)</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg bg-white" data-testid="goal-category">
                {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              {selectedCat?.description && <p className="text-xs text-[#6D8E87] mt-1">{selectedCat.description}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-[#3B5A54] mb-1">Metric (legacy)</label>
              <select value={form.metric_type}
                onChange={e => {
                  const m = availableMetrics.find(x => x.value === e.target.value);
                  setForm({ ...form, metric_type: e.target.value, unit: m?.unit || form.unit });
                }}
                className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg bg-white" data-testid="goal-metric">
                {availableMetrics.map(m => <option key={m.value} value={m.value}>{m.label}{m.auto ? ' (auto)' : ''}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#3B5A54] mb-1">
                Target * {form.metric_type === 'gross_margin_pct' && <span className="text-[#6D8E87] font-normal">(0–100)</span>}
              </label>
              <input
                type="number"
                step="any"
                min={form.metric_type === 'gross_margin_pct' ? 0 : undefined}
                max={form.metric_type === 'gross_margin_pct' ? 100 : undefined}
                value={form.target_value}
                onChange={e => setForm({ ...form, target_value: e.target.value })}
                className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg" required data-testid="goal-target" />
              {form.metric_type === 'gross_margin_pct' && (
                <p className="text-[10px] text-[#6D8E87] mt-1">
                  Tracks live GM % across won deals in the period (executive: own deals; RSM/NSM: self + team). Does not auto-close early.
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-[#3B5A54] mb-1">Unit</label>
              <input type="text" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })}
                placeholder="INR / count / %" className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg" data-testid="goal-unit" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#3B5A54] mb-1">Credit Weight (1–100) *</label>
              <input type="number" min="1" max="100" value={form.credit_weight} onChange={e => setForm({ ...form, credit_weight: e.target.value })}
                className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg" required data-testid="goal-weight" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#3B5A54] mb-2">Period *</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {[
                { v: 'year', l: 'This Year' },
                { v: 'quarter', l: 'This Quarter' },
                { v: 'month', l: 'This Month' },
                { v: 'custom', l: 'Custom' },
              ].map(opt => (
                <button type="button" key={opt.v}
                  onClick={() => opt.v === 'custom' ? setForm({ ...form, period_type: 'custom' }) : applyPreset(opt.v)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition ${form.period_type === opt.v ? 'bg-[#00a69c] text-white border-[#00a69c]' : 'bg-white text-[#3B5A54] border-[#D5E8E4]'}`}
                  data-testid={`period-${opt.v}`}>
                  {opt.l}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#6D8E87] mb-1">Start</label>
                <input type="date" value={form.period_start} onChange={e => setForm({ ...form, period_start: e.target.value, period_type: 'custom' })}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg" required data-testid="goal-start" />
              </div>
              <div>
                <label className="block text-xs text-[#6D8E87] mb-1">End</label>
                <input type="date" value={form.period_end} onChange={e => setForm({ ...form, period_end: e.target.value, period_type: 'custom' })}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg" required data-testid="goal-end" />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#3B5A54] mb-1">Assign To *</label>
            <select value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}
              className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg bg-white" required data-testid="goal-assignee">
              <option value="">Select user...</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name} — {u.role}{u.region ? ` (${u.region})` : ''}</option>)}
            </select>
          </div>

          {/* Weightage-remaining meter. Slice 1 rule: sum of all goals per
              (user, period) must total ≤ 100. This live indicator shows how
              much budget the selected assignee has left in the chosen
              window BEFORE we submit — the server enforces the same cap. */}
          {weightSummary && form.assigned_to && (() => {
            const requested = parseInt(form.credit_weight) || 0;
            const projected = weightSummary.used + requested;
            const over = projected > 100;
            const exact = projected === 100;
            const barPct = Math.min(100, projected);
            return (
              <div
                className={`p-3 rounded-lg border text-xs ${
                  over ? 'bg-rose-50 border-rose-200 text-rose-800'
                       : exact ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                       : 'bg-[#F0F9F7] border-[#D5E8E4] text-[#3B5A54]'
                }`}
                data-testid="weightage-meter"
              >
                <div className="flex justify-between mb-1">
                  <span>Weightage in this period</span>
                  <span className="font-semibold" data-testid="weightage-total">
                    {projected} / 100
                    {weightSummary.used > 0 && <span className="opacity-70"> (existing {weightSummary.used} + this {requested})</span>}
                  </span>
                </div>
                <div className="w-full h-1.5 bg-white/60 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${over ? 'bg-rose-500' : exact ? 'bg-emerald-500' : 'bg-[#00a69c]'}`}
                    style={{ width: `${barPct}%` }}
                  />
                </div>
                {over && (
                  <p className="mt-1 font-medium">Over budget by {projected - 100}. Reduce this weight or trim other goals for the same person + period.</p>
                )}
              </div>
            );
          })()}

          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="goal-error">{error}</div>}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-6 py-3 border border-[#D5E8E4] rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex-1 py-3 bg-[#00a69c] text-white rounded-lg font-semibold hover:bg-[#00bfb3] disabled:opacity-50"
              data-testid="goal-save-btn">
              {saving ? 'Saving...' : (existing ? 'Save Changes' : 'Create Goal')}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Manual progress update modal
const ProgressModal = ({ goal, onClose, onSaved }) => {
  const [value, setValue] = useState(goal.manual_progress || 0);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.patch(`${API}/goals/${goal.id}/progress`, { manual_progress: parseFloat(value) });
      onSaved();
    } catch (err) {
      notify('Failed to update progress');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[420px]" data-testid="progress-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Update Progress</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="text-sm text-[#3B5A54]">
            <span className="font-medium">{goal.title}</span>
            <br />
            <span className="text-xs text-[#6D8E87]">Target: {formatValue(goal.target_value, goal.unit)}</span>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#3B5A54] mb-1">Current Progress ({goal.unit || 'value'})</label>
            <input type="number" step="any" value={value} onChange={e => setValue(e.target.value)}
              className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg" required autoFocus data-testid="progress-input" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-6 py-3 border border-[#D5E8E4] rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex-1 py-3 bg-[#00a69c] text-white rounded-lg font-semibold hover:bg-[#00bfb3] disabled:opacity-50"
              data-testid="progress-save-btn">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};


// ---------------------------------------------------------------------------
// Feb 2026 — Bulk Assign & Templates modals
// ---------------------------------------------------------------------------
// BulkAssignModal: pick a template OR fill inline fields once, then pick N
// assignees. Each row shows the base target inline-editable per-person.
// TemplatesPanel: quick management surface for reusable templates (Use / Edit
// / Archive).

const BulkAssignModal = ({ open, onClose, onSaved, categories, bscOptions, users, templates, prefillTemplate }) => {
  const defaultStart = new Date().toISOString().slice(0, 10);
  const defaultEnd = new Date(new Date().getFullYear(), 11, 31).toISOString().slice(0, 10);
  const [templateId, setTemplateId] = useState(prefillTemplate?.id || '');
  const [form, setForm] = useState({
    title: prefillTemplate?.title || '',
    description: prefillTemplate?.description || '',
    category: prefillTemplate?.category || (categories[0]?.value || categories[0] || 'Financial'),
    metric_type: prefillTemplate?.metric_type || 'revenue',
    base_target_value: prefillTemplate?.base_target_value || 0,
    unit: prefillTemplate?.unit || 'INR',
    credit_weight: prefillTemplate?.credit_weight || 10,
    bsc_parameter: prefillTemplate?.bsc_parameter || 'Financial',
    measure_type: prefillTemplate?.measure_type || 'increasing_number',
    data_source: prefillTemplate?.data_source || null,
    period_type: 'quarter',
    period_start: defaultStart,
    period_end: defaultEnd,
  });
  const [selected, setSelected] = useState({});
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [userRoleFilter, setUserRoleFilter] = useState('all');

  const selectedIds = Object.keys(selected).filter(k => selected[k]?.picked);

  const toggleUser = (u) => setSelected(prev => ({
    ...prev,
    [u.id]: prev[u.id]?.picked ? { picked: false } : { picked: true, target_override: '' },
  }));

  const setOverride = (uid, val) => setSelected(prev => ({
    ...prev,
    [uid]: { ...(prev[uid] || { picked: true }), target_override: val },
  }));

  const bulkPickRole = (role) => {
    const next = { ...selected };
    users.filter(u => u.role === role).forEach(u => { next[u.id] = { picked: true, target_override: '' }; });
    setSelected(next);
  };

  const clearAll = () => setSelected({});

  const filteredUsers = users.filter(u => userRoleFilter === 'all' || u.role === userRoleFilter);
  const uniqueRoles = Array.from(new Set(users.map(u => u.role))).filter(Boolean).sort();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedIds.length === 0) { notify('Pick at least one assignee.'); return; }
    setSubmitting(true);
    try {
      const body = {
        template_id: templateId || null,
        ...(templateId ? {} : {
          title: form.title,
          description: form.description,
          category: form.category,
          metric_type: form.metric_type,
          base_target_value: parseFloat(form.base_target_value) || 0,
          unit: form.unit,
          credit_weight: parseInt(form.credit_weight) || 10,
          bsc_parameter: form.bsc_parameter,
          measure_type: form.measure_type,
          data_source: form.data_source,
        }),
        period_type: form.period_type,
        period_start: form.period_start,
        period_end: form.period_end,
        assignees: selectedIds.map(uid => {
          const override = (selected[uid]?.target_override ?? '').toString().trim();
          return {
            user_id: uid,
            target_value: override === '' ? null : parseFloat(override),
          };
        }),
        save_as_template: !templateId && saveAsTemplate,
        save_as_template_name: templateName || form.title,
      };
      const resp = await axios.post(`${API}/goals/bulk-assign`, body);
      onSaved(resp.data);
    } catch (err) {
      notify(err?.response?.data?.detail || 'Bulk assign failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="bulk-assign-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit' }}>
            <UsersFour size={22} weight="bold" className="inline mr-2 -mt-1" />
            Bulk Assign Goal
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Use a saved template (optional)</label>
            <select
              value={templateId}
              onChange={(e) => {
                const id = e.target.value;
                setTemplateId(id);
                if (id) {
                  const t = templates.find(x => x.id === id);
                  if (t) setForm(f => ({ ...f,
                    title: t.title, description: t.description || '',
                    category: t.category, metric_type: t.metric_type,
                    base_target_value: t.base_target_value, unit: t.unit || '',
                    credit_weight: t.credit_weight, bsc_parameter: t.bsc_parameter,
                    measure_type: t.measure_type, data_source: t.data_source }));
                }
              }}
              className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm bg-white"
              data-testid="bulk-template-select"
            >
              <option value="">— None (define inline below) —</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name} · {t.category} · base {t.base_target_value.toLocaleString('en-IN')}</option>
              ))}
            </select>
          </div>

          {!templateId && (
            <div className="grid grid-cols-2 gap-3 p-3 rounded-lg bg-[#F4F9F7] border border-[#E2ECEA]">
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Goal Title *</label>
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm"
                  data-testid="bulk-title-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Category *</label>
                <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm bg-white"
                  data-testid="bulk-category-select">
                  {categories.map(c => {
                    const val = typeof c === 'string' ? c : c.value;
                    const label = typeof c === 'string' ? c : (c.label || c.value);
                    return <option key={val} value={val}>{label}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Metric Type *</label>
                <input required value={form.metric_type} onChange={(e) => setForm({ ...form, metric_type: e.target.value })}
                  className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Base Target *</label>
                <input required type="number" min="0" step="1" value={form.base_target_value}
                  onChange={(e) => setForm({ ...form, base_target_value: e.target.value })}
                  className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm"
                  data-testid="bulk-target-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Unit</label>
                <input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Weight</label>
                <input type="number" min="1" max="100" value={form.credit_weight}
                  onChange={(e) => setForm({ ...form, credit_weight: e.target.value })}
                  className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#3B5A54] mb-1">BSC Parameter</label>
                <select value={form.bsc_parameter || ''} onChange={(e) => setForm({ ...form, bsc_parameter: e.target.value || null })}
                  className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm bg-white">
                  <option value="">—</option>
                  {(bscOptions.bsc_parameters || []).map(b => {
                    const val = typeof b === 'string' ? b : b.value;
                    const label = typeof b === 'string' ? b : (b.label || b.value);
                    return <option key={val} value={val}>{label}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Measure Type</label>
                <select value={form.measure_type} onChange={(e) => setForm({ ...form, measure_type: e.target.value })}
                  className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm bg-white">
                  {(bscOptions.measure_types || ['increasing_number', 'decreasing_pct']).map(m => {
                    const val = typeof m === 'string' ? m : m.value;
                    const label = typeof m === 'string' ? m : (m.label || m.value);
                    return <option key={val} value={val}>{label}</option>;
                  })}
                </select>
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Period Type</label>
              <select value={form.period_type} onChange={(e) => setForm({ ...form, period_type: e.target.value })}
                className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm bg-white">
                <option value="year">Year</option>
                <option value="quarter">Quarter</option>
                <option value="month">Month</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Start *</label>
              <input required type="date" value={form.period_start}
                onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#3B5A54] mb-1">End *</label>
              <input required type="date" value={form.period_end}
                onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                className="w-full px-3 py-2 border border-[#B8D5CE] rounded-lg text-sm" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-semibold text-[#3B5A54]">Assignees ({selectedIds.length} picked)</label>
              <div className="flex items-center gap-2">
                <select value={userRoleFilter} onChange={(e) => setUserRoleFilter(e.target.value)}
                  className="text-xs px-2 py-1 border border-[#B8D5CE] rounded-md bg-white"
                  data-testid="bulk-role-filter">
                  <option value="all">All roles</option>
                  {uniqueRoles.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                {userRoleFilter !== 'all' && (
                  <button type="button" onClick={() => bulkPickRole(userRoleFilter)}
                    className="text-xs px-2 py-1 border border-[#00a69c] text-[#00a69c] rounded-md hover:bg-[#E2F3F0]"
                    data-testid="bulk-pick-role">
                    Pick all {userRoleFilter}s
                  </button>
                )}
                <button type="button" onClick={clearAll}
                  className="text-xs px-2 py-1 border border-[#B8D5CE] text-[#3B5A54] rounded-md hover:bg-[#F4F9F7]">
                  Clear
                </button>
              </div>
            </div>
            <div className="border border-[#E2ECEA] rounded-lg max-h-64 overflow-y-auto" data-testid="assignee-list">
              {filteredUsers.length === 0 ? (
                <p className="text-xs text-[#6D8E87] p-4 text-center">No users match this filter.</p>
              ) : filteredUsers.map(u => {
                const picked = selected[u.id]?.picked;
                return (
                  <div key={u.id}
                    className={`flex items-center gap-3 px-3 py-2 border-b border-[#E2ECEA] last:border-b-0 ${picked ? 'bg-[#E2F3F0]' : 'hover:bg-[#F4F9F7]'}`}
                    data-testid={`assignee-row-${u.id}`}
                  >
                    <input type="checkbox" checked={!!picked} onChange={() => toggleUser(u)}
                      className="accent-[#00a69c]" data-testid={`assignee-checkbox-${u.id}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#052A23] truncate">{u.name}</p>
                      <p className="text-[11px] text-[#6D8E87] truncate">{u.role}{u.region ? ` · ${u.region}` : ''}</p>
                    </div>
                    {picked && (
                      <input type="number" min="0" step="1" placeholder={`Override (base ${templateId ? templates.find(t=>t.id===templateId)?.base_target_value : form.base_target_value})`}
                        value={selected[u.id]?.target_override ?? ''}
                        onChange={(e) => setOverride(u.id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-40 px-2 py-1 border border-[#B8D5CE] rounded-md text-xs"
                        data-testid={`assignee-override-${u.id}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {!templateId && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <input type="checkbox" checked={saveAsTemplate} onChange={(e) => setSaveAsTemplate(e.target.checked)}
                className="accent-amber-600" id="save-as-template" data-testid="save-as-template-checkbox" />
              <label htmlFor="save-as-template" className="text-xs text-amber-900 flex-1">
                <span className="font-semibold">Save as template</span> for future quarters
              </label>
              {saveAsTemplate && (
                <input type="text" placeholder="Template name (defaults to goal title)"
                  value={templateName} onChange={(e) => setTemplateName(e.target.value)}
                  className="text-xs px-2 py-1 border border-amber-300 rounded-md flex-1"
                  data-testid="save-as-template-name" />
              )}
            </div>
          )}

          <div className="flex gap-2 pt-2 border-t border-[#E2ECEA]">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-[#B8D5CE] text-[#3B5A54] rounded-lg text-sm font-medium">
              Cancel
            </button>
            <button type="submit" disabled={submitting || selectedIds.length === 0}
              className="flex-1 px-4 py-2 bg-[#00a69c] text-white rounded-lg text-sm font-semibold hover:bg-[#00bfb3] disabled:opacity-60"
              data-testid="bulk-submit-button">
              {submitting ? 'Assigning…' : `Assign to ${selectedIds.length} member${selectedIds.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const TemplatesPanel = ({ open, onClose, templates, onUse, onArchive, onRefresh }) => {
  const active = templates.filter(t => !t.is_archived);
  const [seeding, setSeeding] = React.useState(false);

  const seedDefaults = async () => {
    if (seeding) return;
    if (!await confirm({ message: 'Create the 6 standard goal templates? Any that already exist by name will be skipped.' })) return;
    setSeeding(true);
    try {
      const r = await axios.post(`${API}/goal-templates/seed-defaults`, {});
      const parts = [];
      if (r.data.created_count) parts.push(`✓ ${r.data.created_count} created`);
      if (r.data.restored_count) parts.push(`↺ ${r.data.restored_count} restored (were archived)`);
      if (r.data.skipped_count) parts.push(`⚠ ${r.data.skipped_count} already active — skipped`);
      notify(parts.join('\n') || 'Nothing to do.');
      if (onRefresh) await onRefresh();
    } catch (err) {
      notify(err?.response?.data?.detail || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="templates-panel-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit' }}>
            <BookmarkSimple size={22} weight="bold" className="inline mr-2 -mt-1" />
            Goal Templates
          </DialogTitle>
        </DialogHeader>
        {/* Feb 2026 — one-click seeder for the 6 standard templates */}
        <div className="flex justify-end mb-3">
          <button onClick={seedDefaults} disabled={seeding}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border border-[#00a69c] text-[#00a69c] hover:bg-[#E2F3F0] disabled:opacity-60"
            data-testid="seed-default-templates-btn"
            title="One-click bootstrap of the 6 standard goal templates (Financial ×2, Customer ×2, Internal Process, Market Expansion). Idempotent — safe to click any time.">
            {seeding ? 'Setting up…' : '+ Seed 6 standard templates'}
          </button>
        </div>
        {active.length === 0 ? (
          <p className="text-sm text-[#6D8E87] py-8 text-center" data-testid="no-templates">
            No templates yet. Click <span className="font-semibold">Seed 6 standard templates</span> above to bootstrap, or tick “Save as template” next time you use Bulk Assign to add one manually.
          </p>
        ) : (
          <div className="space-y-2">
            {active.map(tpl => (
              <div key={tpl.id} className="border border-[#E2ECEA] rounded-lg p-3 hover:bg-[#F4F9F7] transition"
                data-testid={`template-row-${tpl.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-[#052A23] truncate">{tpl.name}</h4>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#E2F3F0] text-[#00a69c]">{tpl.category}</span>
                      {tpl.bsc_parameter && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">{tpl.bsc_parameter}</span>
                      )}
                    </div>
                    <p className="text-xs text-[#3B5A54] mt-1"><span className="font-medium">Title:</span> {tpl.title}</p>
                    <p className="text-[11px] text-[#6D8E87] mt-0.5">
                      Base target: <span className="font-semibold text-[#052A23]">{tpl.base_target_value.toLocaleString('en-IN')}{tpl.unit ? ` ${tpl.unit}` : ''}</span>
                      {' · '}Weight {tpl.credit_weight}
                      {tpl.data_source ? ` · ${tpl.data_source}` : ''}
                    </p>
                    <p className="text-[10px] text-[#6D8E87] mt-0.5">Created by {tpl.created_by_name}</p>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button onClick={() => onUse(tpl)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#00a69c] text-white hover:bg-[#00bfb3]"
                      data-testid={`use-template-${tpl.id}`}>
                      <UsersFour size={12} weight="bold" /> Use
                    </button>
                    <button onClick={() => onArchive(tpl)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border border-[#B8D5CE] text-[#6D8E87] hover:bg-white"
                      data-testid={`archive-template-${tpl.id}`}>
                      <Archive size={12} weight="bold" /> Archive
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default Goals;
